import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../../app/AuthLayout';
import { Button } from '../../ui/Button';
import { Field, Input, PasswordInput } from '../../ui/Form';
import { Alert } from '../../ui/Feedback';
import { useDocumentTitle, useStrings, useTenant } from '../../tenant/TenantProvider';
import { useTenantSettings } from '../../tenant/useTenantSettings';
import { useAuthStore } from '../../stores/auth';
import { apiRequest } from '../../lib/api';
import { decodeUser } from '../../lib/decoders';

export function LoginPage() {
  const t = useStrings();
  const { brand, features } = useTenant();
  const settings = useTenantSettings();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [params] = useSearchParams();
  const verification = params.get('verification');
  const verificationNotice = ['success', 'invalid-token', 'missing-token', 'server-error'].includes(verification)
    ? { tone: verification === 'success' ? 'success' : 'danger', message: t(`auth.verification.${verification}`) }
    : null;
  const notice = location.state?.notice ?? verificationNotice;

  useDocumentTitle(t('auth.signIn'));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const response = await apiRequest('/auth/v2/login', { method: 'POST', body: form }, decodeUser);
    setSubmitting(false);
    if (!response.success) {
      setError(response.error);
      return;
    }
    setAuth(response.data);
  };

  const footer = !settings ? null : settings.signupEnabled ? (
    <>
      {t('auth.signupPrompt')}{' '}
      <Link to="/signup" className="text-link">
        {t('auth.signupLink')}
      </Link>
    </>
  ) : brand.organization ? (
    t('auth.accountsManagedBy')
  ) : (
    t('auth.accountsManaged')
  );

  return (
    <AuthLayout title={t('auth.signInTitle')} subtitle={t('auth.signInSubtitle')} footer={footer}>
      {notice && <Alert tone={notice.tone ?? 'info'}>{notice.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      <form className="auth__form" onSubmit={submit} noValidate>
        <Field label={t('auth.email')} htmlFor="login-email">
          <Input
            id="login-email"
            type="email"
            size="lg"
            autoComplete="email"
            autoFocus
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>
        <Field
          label={t('auth.password')}
          htmlFor="login-password"
          action={
            features.forgotPassword !== false && (
              <Link to="/forgot-password" className="text-link field__hint">
                {t('auth.forgotPassword')}
              </Link>
            )
          }
        >
          <PasswordInput
            id="login-password"
            size="lg"
            autoComplete="current-password"
            required
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            showLabel={t('auth.showPassword')}
            hideLabel={t('auth.hidePassword')}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" block loading={submitting} disabled={!form.email || !form.password}>
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>
    </AuthLayout>
  );
}
